import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { Provider } from 'react-redux';
import { router } from 'components/routes/Router';
import { ErrorBoundary } from 'components/molecules/ErrorBoundary';
import { SubscriptionExpire } from 'components/organisms/SubscriptionExpire';
import { ScriptLoader } from 'components/molecules/ScriptLoader';
import configureStore from 'config/store';
import { ToastContainer, Zoom } from 'react-toastify';
import FullScreenLoader from 'components/Loader/FullScreenLoader';
import UserProvider from 'contexts/user.context';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from 'lib/queryClient';
import { WebSocketProvider } from 'contexts/webSocketcontext';
import { WS_API_URL } from 'config/constants';
import { initAmplitude } from './utils/Analytics/amplitude';
import { initPostHog } from './utils/Analytics/posthog';
import 'lib/instrumentation';
import 'react-toastify/dist/ReactToastify.css';
import './assets/scss/style.scss';
import './assets/css/style.css';
import './utils/observability/webVitals';
import { AppVersionUpdateListener } from 'components/molecules/AppVersionUpdateListener';
import {
  clearVitePreloadReloadGuard,
  handleVitePreloadError,
} from 'utils/observability/vite';
import { initNavigationTracker } from 'utils/observability/navigationTracker';
// Commented out for now as it is not working as expected
// import { NoInternetConnection } from 'components/molecules/NoInternetConnection';

export const store = configureStore();

function App() {
  useEffect(() => {
    initAmplitude();
    initPostHog();
    // Capture the unsubscribe handle so the router subscription is cleaned up when the effect tears down (HMR, unmount).
    const unsubscribeNavTracker = initNavigationTracker(router);

    // Delay clearing the guard so it remains active while React Router finishes
    // lazy-loading all route chunks. Clearing immediately would allow a second
    // preload error (e.g. a second broken chunk) to trigger another full reload
    // before the page has stabilised — which is exactly what caused the 3-reload
    // loop reported for some users. 12 s gives every lazy route time to load.
    const guardTimer = window.setTimeout(clearVitePreloadReloadGuard, 12_000);

    window.addEventListener('vite:preloadError', handleVitePreloadError);
    return () => {
      window.clearTimeout(guardTimer);
      window.removeEventListener('vite:preloadError', handleVitePreloadError);
      unsubscribeNavTracker(); // Releases the router subscription
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Commented out for now as it is not working as expected 
      <NoInternetConnection /> */}
      <Provider store={store}>
        <UserProvider>
          <ErrorBoundary>
            <WebSocketProvider url={`${WS_API_URL}/ws/download/`}>
              <ErrorBoundary>
                <RouterProvider router={router} />

                <ToastContainer
                  position="top-right"
                  autoClose={3000}
                  hideProgressBar={false}
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss={false}
                  draggable
                  theme="light"
                  pauseOnHover
                  transition={Zoom}
                  limit={2}
                />

                <ErrorBoundary height={100}>
                  <SubscriptionExpire />
                  <AppVersionUpdateListener />
                  <FullScreenLoader />
                  <ScriptLoader />
                </ErrorBoundary>
              </ErrorBoundary>
            </WebSocketProvider>
          </ErrorBoundary>
        </UserProvider>
      </Provider>
    </QueryClientProvider>
  );
}

export default App;
